/**
 * One resource group at full size, with the arrears burn-down drawn over it and a flag on
 * the week it clears.
 *
 * The line is the reason this chart exists rather than a bigger version of the small
 * multiple: the bars say what each week is spent on, and the line says whether any of it is
 * working. A row can be at 100% every week and still be falling further behind.
 *
 * Suppressed entirely in the as-scheduled view — there is no burn-down to draw when nothing
 * has been levelled, and a line implying one would be an invention.
 */

import React from 'react'
import type { ResourceModel, WeekModel } from '../../viewmodels/loading'
import { columnLabel, hrs, levelledSegs, scheduledSegs, tickEvery, weekLabel } from './format'

const PLOT = 206

function BurnDown({ r }: { r: ResourceModel }): React.JSX.Element {
  const max = Math.max(r.overdue, ...r.weeks.map((w) => w.carry), 1)
  const points = r.weeks
    .map((w, i) => `${(((i + 0.5) / r.weeks.length) * 100).toFixed(2)},${(100 - (w.carry / max) * 94).toFixed(2)}`)
    .join(' ')
  const shared = {
    points, fill: 'none', vectorEffect: 'non-scaling-stroke' as const,
    strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const,
  }
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[5] h-full w-full overflow-visible"
    >
      <polyline {...shared} stroke="var(--lc-panel)" strokeWidth="4.5" strokeOpacity="0.85" />
      <polyline {...shared} stroke="var(--lc-red)" strokeWidth="2" />
    </svg>
  )
}

function Column({ w, r, levelled, scale, selected, onSelect }: {
  w: WeekModel
  r: ResourceModel
  levelled: boolean
  scale: number
  selected: boolean
  onSelect: (index: number) => void
}): React.JSX.Element {
  const segs = levelled ? levelledSegs(w, scale, false) : scheduledSegs(w, r.overdue, scale)
  return (
    <button
      type="button"
      aria-label={columnLabel(w, levelled)}
      aria-pressed={selected}
      onClick={() => onSelect(w.index)}
      className="flex-1 rounded-[3px]"
      style={{ outline: selected ? '2px solid var(--lc-blue)' : 'none', outlineOffset: 2 }}
    >
      <span className="flex flex-col-reverse justify-start overflow-hidden rounded-t-[3px]" style={{ height: PLOT }}>
        {segs.map((s) => <span key={s.key} data-seg={s.key} className={s.className} style={s.style} />)}
      </span>
    </button>
  )
}

function ChartHead({ r, levelled }: { r: ResourceModel; levelled: boolean }): React.JSX.Element {
  const clears = r.clearIdx >= 0
  return (
    <div className="flex items-baseline justify-between gap-4 pb-3">
      <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.09em]" style={{ color: 'var(--lc-mut)' }}>
        {levelled ? 'Weekly capacity, and what it is spent on' : 'Work as scheduled against capacity'}
      </span>
      <span
        className="whitespace-nowrap rounded-[5px] px-2 py-1 font-mono text-[10px] font-semibold tracking-[.05em]"
        style={{
          background: clears ? 'var(--lc-teal-bg)' : 'var(--lc-red-bg)',
          color: clears ? 'var(--lc-teal-ink)' : 'var(--lc-red-ink)',
        }}
      >
        {clears
          ? `backlog clears w/c ${weekLabel(r.weeks[r.clearIdx].weekStart)}`
          : `${hrs(r.queuedAtEnd)} still queued at week ${r.weeks.length}`}
      </span>
    </div>
  )
}

function Plot({ r, levelled, scale, week, onSelect }: {
  r: ResourceModel
  levelled: boolean
  scale: number
  week: number
  onSelect: (index: number) => void
}): React.JSX.Element {
  const ruleTop = PLOT * (1 - (scale ? r.capacity / scale : 0))
  return (
    <div className="relative" style={{ height: PLOT }}>
      <div className="pointer-events-none absolute inset-x-0 z-[6] border-t border-dashed" style={{ top: ruleTop, borderColor: 'var(--lc-rule)' }} />
      <div
        className="absolute left-0 z-[6] pr-[5px] font-mono text-[8.5px] font-semibold uppercase tracking-[.06em]"
        style={{ top: ruleTop - 14, color: 'var(--lc-mut)', background: 'var(--lc-panel)' }}
      >
        capacity {hrs(r.capacity)}/wk
      </div>
      <div className="absolute inset-0 z-[1] flex items-end gap-[5px]">
        {r.weeks.map((w) => (
          <Column key={w.weekStart} {...{ w, r, levelled, scale, onSelect }} selected={w.index === week} />
        ))}
      </div>
      {levelled && <BurnDown r={r} />}
      {levelled && r.clearIdx >= 0 && (
        <div
          className="absolute top-[2px] z-[7] -translate-x-1/2 whitespace-nowrap rounded-[4px] border px-1.5 py-[2px] font-mono text-[9px] font-semibold uppercase tracking-[.06em]"
          style={{
            left: `${((r.clearIdx + 0.5) / r.weeks.length) * 100}%`,
            color: 'var(--lc-teal-ink)', background: 'var(--lc-teal-bg)', borderColor: 'var(--lc-teal-line)',
          }}
        >
          backlog clears here
        </div>
      )}
    </div>
  )
}

export default function ResourceChart({ r, levelled, week, onSelect, children }: {
  r: ResourceModel
  levelled: boolean
  week: number
  onSelect: (index: number) => void
  children: React.ReactNode
}): React.JSX.Element {
  const peak = Math.max(...r.weeks.map((w) => w.scheduled), 1)
  const scale = levelled ? r.capacity * 1.3 : peak * 1.06
  // Thinned on a long horizon (format.tickEvery), EXCEPT the selected week, which is always
  // named: it is the week the panel below is showing, and it is the one date a reader needs.
  const every = tickEvery(r.weeks.length)
  return (
    <div className="rounded-[10px] border px-4 pb-2.5 pt-3.5" style={{ borderColor: 'var(--lc-b09)', background: 'var(--lc-panel)' }}>
      <ChartHead r={r} levelled={levelled} />
      <Plot r={r} levelled={levelled} scale={scale} week={week} onSelect={onSelect} />
      <div className="flex gap-[5px] pt-[7px]">
        {r.weeks.map((w) => (
          <div
            key={w.weekStart}
            className={`flex-1 text-center font-mono text-[9px] ${w.index === week ? 'font-semibold' : 'font-medium'}`}
            style={{ color: w.index === week ? 'var(--lc-blue)' : 'var(--lc-faint)' }}
          >
            {w.index % every === 0 || w.index === week ? weekLabel(w.weekStart) : '\u00a0'}
          </div>
        ))}
      </div>
      <div className="mt-2.5 border-t pt-3" style={{ borderColor: 'var(--lc-b07)' }}>{children}</div>
    </div>
  )
}
